/**
 * Shared product info popup used across the purchase, build-your-own, PDP and
 * post-checkout flows. Renders the product image gallery (carousel when a
 * product has multiple images), description and ingredients, with tap-to-zoom
 * on the image. Exposes window.ProductInfoPopup for flows that open the popup
 * from their own product data (e.g. post-checkout builders).
 */
(function () {
  'use strict';

  // Guard against the asset being included more than once on a page, which
  // would otherwise register the delegated handlers twice.
  if (window.ProductInfoPopup) {
    return;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (character) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[character];
    });
  }

  function normalizeImages(images, fallbackSrc, fallbackAlt) {
    var list = Array.isArray(images)
      ? images.filter(function (image) { return image && image.src; })
      : [];
    if (list.length) {
      return list;
    }
    return fallbackSrc ? [{ src: fallbackSrc, alt: fallbackAlt || '' }] : [];
  }

  function parseImageList(imageListJson) {
    if (!imageListJson) {
      return [];
    }
    try {
      var parsed = JSON.parse(imageListJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('Could not parse product popup images', error);
      return [];
    }
  }

  function buildGallery(images, title) {
    if (!images.length) {
      return '';
    }

    var slides = images.map(function (image, index) {
      return (
        '<img src="' + escapeHtml(image.src) + '"' +
        ' alt="' + escapeHtml(image.alt || title) + '"' +
        ' class="product-info-popup-slide zoomable' + (index === 0 ? ' active' : '') + '"' +
        ' data-slide-index="' + index + '">'
      );
    }).join('');

    var controls = '';
    if (images.length > 1) {
      var dots = images.map(function (image, index) {
        return (
          '<button type="button"' +
          ' class="product-info-popup-carousel-dot' + (index === 0 ? ' active' : '') + '"' +
          ' data-popup-carousel-dot="' + index + '"' +
          ' aria-current="' + (index === 0 ? 'true' : 'false') + '"' +
          ' aria-label="Show image ' + (index + 1) + '"></button>'
        );
      }).join('');

      controls =
        '<div class="product-info-popup-carousel-controls" aria-label="Product images">' +
          '<button type="button" class="product-info-popup-carousel-button" data-popup-carousel-prev aria-label="Previous image">&lsaquo;</button>' +
          '<div class="product-info-popup-carousel-dots">' + dots + '</div>' +
          '<button type="button" class="product-info-popup-carousel-button" data-popup-carousel-next aria-label="Next image">&rsaquo;</button>' +
        '</div>';
    }

    return (
      '<div class="product-info-popup-gallery">' +
        '<div class="product-info-popup-image">' + slides + '</div>' +
        controls +
      '</div>'
    );
  }

  function setupCarousel(popup) {
    var slides = popup.querySelectorAll('.product-info-popup-slide');
    if (slides.length < 2) {
      return;
    }

    var dots = popup.querySelectorAll('[data-popup-carousel-dot]');
    var prevButton = popup.querySelector('[data-popup-carousel-prev]');
    var nextButton = popup.querySelector('[data-popup-carousel-next]');
    var activeIndex = 0;

    function show(index) {
      activeIndex = (index + slides.length) % slides.length;
      slides.forEach(function (slide, slideIndex) {
        slide.classList.toggle('active', slideIndex === activeIndex);
      });
      dots.forEach(function (dot, dotIndex) {
        dot.classList.toggle('active', dotIndex === activeIndex);
        dot.setAttribute('aria-current', dotIndex === activeIndex ? 'true' : 'false');
      });
    }

    if (prevButton) {
      prevButton.addEventListener('click', function () { show(activeIndex - 1); });
    }
    if (nextButton) {
      nextButton.addEventListener('click', function () { show(activeIndex + 1); });
    }
    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        show(parseInt(dot.getAttribute('data-popup-carousel-dot'), 10));
      });
    });

    // Touch swipe support for mobile
    var touchStartX = 0;
    var touchStartY = 0;
    var touchEndX = 0;
    var touchEndY = 0;
    var gallery = popup.querySelector('.product-info-popup-image');

    if (gallery) {
      gallery.addEventListener('touchstart', function (event) {
        if (event.touches.length === 1) {
          touchStartX = event.touches[0].screenX;
          touchStartY = event.touches[0].screenY;
        }
      }, { passive: true });

      gallery.addEventListener('touchend', function (event) {
        if (event.changedTouches.length === 1) {
          touchEndX = event.changedTouches[0].screenX;
          touchEndY = event.changedTouches[0].screenY;
          handleSwipe();
        }
      }, { passive: true });
    }

    function handleSwipe() {
      var diffX = touchEndX - touchStartX;
      var diffY = touchEndY - touchStartY;
      
      // Ensure horizontal swipe is dominant and exceeds 40px threshold
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 40) {
        if (diffX > 0) {
          show(activeIndex - 1); // Swiped right (previous)
        } else {
          show(activeIndex + 1); // Swiped left (next)
        }
      }
    }
  }

  function close(popup) {
    if (popup && popup.parentNode) {
      popup.parentNode.removeChild(popup);
    }
    document.body.classList.remove('popup-open');
    document.documentElement.classList.remove('html-popup-open');
  }

  function open(options) {
    options = options || {};
    var title = options.title || '';
    var images = normalizeImages(options.images, options.imageUrl, title);
    var descriptionHtml = options.descriptionHtml || '';
    var ingredientsHtml = options.ingredientsHtml || '';

    var existing = document.querySelector('.product-info-popup');
    if (existing) {
      close(existing);
    }

    var popup = document.createElement('div');
    popup.className = 'product-info-popup';
    popup.innerHTML =
      '<div class="product-info-popup-content">' +
        '<button type="button" class="product-info-popup-close" aria-label="Close">&times;</button>' +
        '<h2>' + escapeHtml(title) + '</h2>' +
        '<div class="product-info-popup-columns">' +
          buildGallery(images, title) +
          '<div class="product-info-popup-description">' +
            (descriptionHtml ? '<div class="product-info-popup-description-text">' + descriptionHtml + '</div>' : '') +
            (ingredientsHtml ? '<div class="product-info-popup-description-metafields"><br>' + ingredientsHtml + '</div>' : '') +
          '</div>' +
        '</div>' +
      '</div>';

    document.body.appendChild(popup);
    document.body.classList.add('popup-open');
    document.documentElement.classList.add('html-popup-open');

    popup.querySelector('.product-info-popup-close').addEventListener('click', function () { close(popup); });
    popup.addEventListener('click', function (event) {
      if (event.target === popup) {
        close(popup);
      }
    });

    setupCarousel(popup);
    return popup;
  }

  function openFromButton(button) {
    if (!button) {
      return;
    }
    var product = {};
    try {
      product = JSON.parse(button.getAttribute('data-product-json')) || {};
    } catch (error) {
      console.warn('Product JSON parse failed', error);
    }
    open({
      title: product.title || product.name || '',
      images: normalizeImages(
        parseImageList(button.getAttribute('data-image-list')),
        button.getAttribute('data-image-url'),
        product.title
      ),
      descriptionHtml: product.description || product.body_html || '',
      ingredientsHtml: button.getAttribute('data-ingredients-metafields') || ''
    });
  }

  function openZoom(image) {
    var existing = document.querySelector('.zoom-overlay');
    if (existing) {
      existing.remove();
    }

    var overlay = document.createElement('div');
    overlay.className = 'zoom-overlay';
    overlay.innerHTML =
      '<button type="button" class="zoom-close" aria-label="Close">&times;</button>' +
      '<img src="' + escapeHtml(image.currentSrc || image.src) + '" alt="' + escapeHtml(image.alt || '') + '">';

    var zoomImg = overlay.querySelector('img');
    var closeBtn = overlay.querySelector('.zoom-close');

    var closeOverlay = function () { overlay.remove(); };
    closeBtn.addEventListener('click', closeOverlay);

    // Zoom and pan state
    var currentScale = 1;
    var startScale = 1;
    var initialTouchDistance = 0;
    
    var translateX = 0;
    var translateY = 0;
    var startTranslateX = 0;
    var startTranslateY = 0;
    
    var lastTouchX = 0;
    var lastTouchY = 0;
    var isDragging = false;
    var touchCount = 0;
    var hasMoved = false;
    var wasMultiTouch = false;

    function getDistance(t1, t2) {
      return Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
    }

    function updateTransform(animate) {
      zoomImg.style.transition = animate ? 'transform 0.2s ease-out' : 'none';
      zoomImg.style.transform = 'translate(' + translateX + 'px, ' + translateY + 'px) scale(' + currentScale + ')';
    }

    overlay.addEventListener('touchstart', function (event) {
      touchCount = event.touches.length;
      
      if (touchCount === 1) {
        isDragging = true;
        hasMoved = false;
        wasMultiTouch = false;
        lastTouchX = event.touches[0].clientX;
        lastTouchY = event.touches[0].clientY;
        startTranslateX = translateX;
        startTranslateY = translateY;
      } else if (touchCount === 2) {
        isDragging = false;
        wasMultiTouch = true;
        startScale = currentScale;
        initialTouchDistance = getDistance(event.touches[0], event.touches[1]);
        startTranslateX = translateX;
        startTranslateY = translateY;
      }
      
      if (event.target === zoomImg || event.target === overlay) {
        event.preventDefault();
      }
    }, { passive: false });

    overlay.addEventListener('touchmove', function (event) {
      if (event.touches.length === 1 && isDragging && currentScale > 1) {
        var touchX = event.touches[0].clientX;
        var touchY = event.touches[0].clientY;
        var deltaX = touchX - lastTouchX;
        var deltaY = touchY - lastTouchY;
        
        if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
          hasMoved = true;
        }
        
        translateX = startTranslateX + deltaX;
        translateY = startTranslateY + deltaY;
        updateTransform(false);
      } else if (event.touches.length === 2 && initialTouchDistance > 0) {
        var currentTouchDistance = getDistance(event.touches[0], event.touches[1]);
        var scaleFactor = currentTouchDistance / initialTouchDistance;
        
        currentScale = Math.min(Math.max(startScale * scaleFactor, 1), 5);
        hasMoved = true;
        
        if (currentScale === 1) {
          translateX = 0;
          translateY = 0;
        }
        updateTransform(false);
      }
      
      if (event.target === zoomImg || event.target === overlay) {
        event.preventDefault();
      }
    }, { passive: false });

    overlay.addEventListener('touchend', function (event) {
      touchCount = event.touches.length;
      if (touchCount === 0) {
        isDragging = false;
        initialTouchDistance = 0;
      } else if (touchCount === 1) {
        isDragging = true;
        lastTouchX = event.touches[0].clientX;
        lastTouchY = event.touches[0].clientY;
        startTranslateX = translateX;
        startTranslateY = translateY;
      }
    }, { passive: false });

    // Handle double-tap to zoom in/out
    var lastTapTime = 0;
    zoomImg.addEventListener('touchend', function (event) {
      if (wasMultiTouch) {
        return;
      }
      var currentTime = new Date().getTime();
      var tapDelay = currentTime - lastTapTime;
      if (tapDelay < 300 && tapDelay > 0) {
        if (currentScale > 1) {
          currentScale = 1;
          translateX = 0;
          translateY = 0;
        } else {
          currentScale = 2.5;
        }
        updateTransform(true);
        event.preventDefault();
      }
      lastTapTime = currentTime;
    });

    // Close on click / tap (only if they didn't drag/swipe)
    overlay.addEventListener('click', function (event) {
      if (hasMoved) {
        return;
      }
      // Clicking the backdrop closes the overlay. Tapping the image only closes
      // it if we are at normal scale (1x), preventing accidental close on pinch release.
      if (event.target === overlay || (event.target === zoomImg && currentScale === 1)) {
        closeOverlay();
      }
    });

    document.body.appendChild(overlay);
  }

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    var zoomImage = event.target.closest('.product-info-popup img, .zoomable');
    if (zoomImage) {
      event.stopPropagation();
      openZoom(zoomImage);
      return;
    }

    var button = event.target.closest('.purchase-flow__product-card-more-info');
    if (!button) {
      var trigger = event.target.closest(
        '.purchase-flow__product-card__image, .purchase-flow__product-card__description, .purchase-flow__product-title'
      );
      var card = trigger && trigger.closest('.purchase-flow__product-card');
      button = card ? card.querySelector('.purchase-flow__product-card-more-info') : null;
    }

    if (button) {
      event.stopPropagation();
      openFromButton(button);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') {
      return;
    }
    var zoomOverlay = document.querySelector('.zoom-overlay');
    if (zoomOverlay) {
      zoomOverlay.remove();
      return;
    }
    var popup = document.querySelector('.product-info-popup');
    if (popup) {
      close(popup);
    }
  });

  window.ProductInfoPopup = {
    open: open,
    openFromButton: openFromButton,
    close: close
  };
})();
